if (!customElements.get('blako-product-tabs')) {
  class BlakoProductTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-bpt-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-bpt-panel]'));
      this.prevButton = this.querySelector('[data-bpt-prev]');
      this.nextButton = this.querySelector('[data-bpt-next]');
      this.tabScroller = this.querySelector('[data-bpt-tab-scroller]');

      if (!this.tabs.length || !this.panels.length) return;

      this.onTabClick = (event) => this.activateTab(event.currentTarget.dataset.bptTab, true);
      this.onTabKeydown = (event) => this.handleTabKeydown(event);
      this.onPrevious = () => this.scrollProducts(-1);
      this.onNext = () => this.scrollProducts(1);
      this.onTrackScroll = () => this.updateControls();
      this.onResize = () => this.updateControls();
      this.onBlockSelect = (event) => this.handleBlockSelect(event);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      });

      this.prevButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      this.attachTrackListener();
      requestAnimationFrame(() => this.updateControls());
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      });
      this.prevButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.activeTrack?.removeEventListener('scroll', this.onTrackScroll);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    get activePanel() {
      return this.panels.find((panel) => !panel.hidden);
    }

    getActiveTrack() {
      return this.activePanel?.querySelector('[data-bpt-track]') || null;
    }

    attachTrackListener() {
      this.activeTrack?.removeEventListener('scroll', this.onTrackScroll);
      this.activeTrack = this.getActiveTrack();
      this.activeTrack?.addEventListener('scroll', this.onTrackScroll, { passive: true });
    }

    activateTab(key, focusTab = false) {
      const selectedTab = this.tabs.find((tab) => tab.dataset.bptTab === key);
      const selectedPanel = this.panels.find((panel) => panel.dataset.bptPanel === key);
      if (!selectedTab || !selectedPanel) return;

      this.tabs.forEach((tab) => {
        const active = tab === selectedTab;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel !== selectedPanel;
      });

      this.attachTrackListener();
      if (this.activeTrack) this.activeTrack.scrollLeft = 0;

      selectedTab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      if (focusTab) selectedTab.focus({ preventScroll: true });

      requestAnimationFrame(() => this.updateControls());
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.tabs.length - 1;

      if (nextIndex === null) return;
      event.preventDefault();
      this.activateTab(this.tabs[nextIndex].dataset.bptTab, true);
    }

    handleBlockSelect(event) {
      const selectedTab = event.target.closest('[data-bpt-tab]');
      if (selectedTab && this.contains(selectedTab)) {
        this.activateTab(selectedTab.dataset.bptTab, false);
        return;
      }

      const item = event.target.closest('[data-bpt-item]');
      if (!item || !this.contains(item)) return;
      const panel = item.closest('[data-bpt-panel]');
      if (panel) this.activateTab(panel.dataset.bptPanel, false);
    }

    scrollProducts(direction) {
      const track = this.getActiveTrack();
      const firstItem = track?.querySelector('[data-bpt-item]');
      if (!track || !firstItem) return;

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
      const distance = firstItem.getBoundingClientRect().width + gap;
      track.scrollBy({ left: distance * direction, behavior: 'smooth' });
    }

    updateControls() {
      if (!this.prevButton || !this.nextButton) return;
      const track = this.getActiveTrack();
      if (!track) {
        this.prevButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const current = Math.max(0, track.scrollLeft);
      const threshold = 3;

      this.prevButton.disabled = current <= threshold;
      this.nextButton.disabled = current >= maxScroll - threshold;
    }
  }

  customElements.define('blako-product-tabs', BlakoProductTabs);
}
